import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useContext } from "react";
import { Link } from "react-router";
import { LoadingSpinner } from "../bits/loading";
import { useUser } from "../hooks/useUser";
import { UserPublicationsContext } from "../contexts/WithPublications/UserPublicationsContext";
import SearchIcon from "@assets/search.svg";

const RecentSearchItem = styled(Link)<{ homeView?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 12px;
  box-shadow:
    0px 0px 2px 1px rgba(255, 255, 255, 1) inset,
    0px 0px 0px 0.25px rgba(234, 234, 238, 1) inset,
    0px 2px 4px rgba(9, 9, 11, 0.08),
    0px 0px 4px rgba(224, 224, 230, 1);
  background-color: ${({ theme }) => theme.colours.uIGrey.light._10};
  width: 100%;
  overflow: hidden;

  ${({ homeView, theme }) =>
    homeView
      ? `
      width: calc(50% - 8px);

      @media (max-width: ${theme.breakpoints.values.md}px) {
        width: 100%;
      }
    `
      : ""}

  :hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

const RecentSearchLocation = ({
  publicationTitle,
}: {
  publicationTitle?: string;
}) => (
  <div
    css={({ customTypography }) => css`
      ${customTypography.label._4}
      color:rgba(73, 76, 92, 1);
      display: flex;
      align-items: center;

      gap: 2px;
      padding: 1px 4px 1px 1px;

      height: 16px;
      box-shadow:
        0 0 12px -6px rgba(181, 184, 196, 1),
        0 -6px 12px -2px rgba(255, 255, 255, 1) inset,
        0 0 0 0.5px rgba(244, 244, 246, 1) inset,
        0 0 4px 2px rgba(181, 184, 196, 1) inset;
      border-radius: 8px;
      background-color: rgba(224, 224, 230, 1);
    `}
  >
    <div
      css={css`
        width: 14px;
        height: 14px;
        box-shadow:
          0 0 4px rgba(198, 200, 210, 1),
          0 0 0 0.25px rgba(198, 200, 210, 1) inset,
          0 0 2px 1px rgba(234, 234, 238, 1) inset;
        background-color: rgba(224, 224, 230, 1);
        border-radius: 8px;

        display: flex;
        justify-content: center;
        align-items: center;
      `}
    >
      <img src={SearchIcon} alt="Search" width={8} height={8} />
    </div>
    <div>{publicationTitle ?? "All publications"}</div>
  </div>
);

export const RecentActivity = ({
  className,
  homeView,
}: {
  className?: string;
  homeView?: boolean;
}) => {
  const { user } = useUser();
  const { publications, isLoading } = useContext(UserPublicationsContext);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div
      css={css`
        display: flex;
        justify-content: flex-start;
        flex-direction: ${homeView ? "row" : "column"};

        width: 100%;
        flex-wrap: wrap;
        gap: 16px;
        padding: 22px 4px;
      `}
      className={className}
    >
      {user?.recentSearches.map((recentSearches) => {
        const publication =
          typeof recentSearches.publication === "number"
            ? publications?.find((pub) => pub.id === recentSearches.publication)
            : undefined;
        return (
          <RecentSearchItem
            homeView={homeView}
            to={
              publication?.filename
                ? `/pub/${publication.filename}?search=${recentSearches.query}`
                : `/search?search=${recentSearches.query}`
            }
            key={recentSearches.query + recentSearches.publication}
            css={css`
              text-decoration: none;
            `}
            title={recentSearches.query}
          >
            <img src={SearchIcon} alt="Search" width={28} height={28} />
            <div
              css={css`
                display: flex;
                justify-content: center;
                flex-direction: column;
                overflow: hidden;
              `}
            >
              <div
                css={({ customTypography }) => css`
                  ${customTypography.jumbo._5}
                  color: rgba(59, 62, 75, 1);
                  overflow: hidden;
                  white-space: nowrap;
                  text-overflow: ellipsis;
                `}
              >
                {recentSearches.query}
              </div>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 4px;
                `}
              >
                <div
                  css={({ customTypography }) => css`
                    ${customTypography.label._4}
                    color: rgba(73, 76, 92, 1);
                  `}
                >
                  In
                </div>
                <RecentSearchLocation publicationTitle={publication?.title} />
              </div>
            </div>
          </RecentSearchItem>
        );
      })}
    </div>
  );
};
